import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Search, ArrowRight, ShieldCheck, FileCheck, Wrench,
  CreditCard, Phone, MapPin, CheckCircle2, Car, Gauge, Fuel, Settings2,
  LifeBuoy, FileSignature
} from 'lucide-react';
import { carsAPI } from '../services/api';
import CarCard from '../components/CarCard';
import Testimonials from '../components/Testimonials';
import { estimateMonthly } from '../utils/financing';
import Seo from '../components/Seo';

const Home = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [cars, setCars] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchMake, setSearchMake] = useState('');
  const [searchMaxPrice, setSearchMaxPrice] = useState('');

  useEffect(() => {
    let mounted = true;
    carsAPI.getAll()
      .then((res) => { if (mounted) setCars(res.data || []); })
      .catch((err) => console.error(err))
      .finally(() => { if (mounted) setIsLoading(false); });
    return () => { mounted = false; };
  }, []);

  const featured = cars.filter((c) => c.status !== 'sold').slice(0, 8);

  // Latest arrival: prefer flagged, else first non-sold car
  const latestArrival = cars.find((c) => c.is_latest_arrival) || cars.find((c) => c.status !== 'sold');

  const onSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchMake) params.set('make', searchMake);
    if (searchMaxPrice) params.set('maxPrice', searchMaxPrice);
    navigate(`/inventory${params.toString() ? `?${params}` : ''}`);
  };

  const trustItems = [
    { icon: ShieldCheck, key: 'why.quality', title: t('why.quality.title'), desc: t('why.quality.desc') },
    { icon: LifeBuoy, key: 'why.assistance', title: t('why.assistance.title'), desc: t('why.assistance.desc') },
    { icon: FileSignature, key: 'why.insurance', title: t('why.insurance.title'), desc: t('why.insurance.desc') },
    { icon: FileCheck, key: 'why.carpass', title: t('why.carpass.title', 'Car-Pass remis'), desc: t('why.carpass.desc', 'Historique kilométrique officiel pour chaque véhicule.') },
    { icon: Wrench, key: 'why.tech', title: t('why.maintenance.title'), desc: t('why.maintenance.desc') },
    { icon: CreditCard, key: 'why.financing', title: t('why.financing.title'), desc: t('why.financing.desc') },
  ];

  return (
    <div className="bg-[#F7F8FA] pt-16 lg:pt-20" data-testid="home-page">
      <Seo title={t('seo.home.title')} description={t('seo.home.desc')} />
      {/* ===== HERO + SEARCH ===== */}
      <section className="relative bg-white border-b border-slate-200" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-10 lg:pt-20 lg:pb-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div className="fade-up">
              <span className="section-eyebrow">
                {t('hero.eyebrow', 'Garage agréé — Wavre, Belgique')}
              </span>
              <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 leading-[1.05]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {t('hero.titleA', 'Achetez votre prochaine voiture')}{' '}
                <span className="text-blue-900">{t('hero.titleB', 'en toute confiance')}</span>.
              </h1>
              <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl">
                {t('hero.description', "Véhicules d'occasion contrôlés, garantie 1 an incluse, Car-Pass remis. Visite uniquement sur rendez-vous.")}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/inventory" className="btn-primary" data-testid="hero-view-inventory">
                  {t('hero.viewInventory', 'Voir l\'inventaire')}
                  <ArrowRight size={16} />
                </Link>
                <Link to="/booking" className="btn-secondary" data-testid="hero-book-appointment">
                  {t('hero.bookAppointment', 'Prendre rendez-vous')}
                </Link>
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-slate-600">
                <span className="inline-flex items-center gap-1.5"><ShieldCheck size={16} className="text-emerald-500" /> {t('hero.trust1', 'Garantie 1 an')}</span>
                <span className="inline-flex items-center gap-1.5"><FileCheck size={16} className="text-emerald-500" /> {t('hero.trust2', 'Car-Pass')}</span>
                <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={16} className="text-emerald-500" /> {t('hero.trust3', 'Contrôle technique récent')}</span>
              </div>
            </div>

            <div className="relative" data-testid="hero-latest-arrival-wrapper">
              {!isLoading && latestArrival ? (
                <Link
                  to={`/inventory/${latestArrival.id}`}
                  className="block group"
                  data-testid="hero-latest-arrival-card"
                >
                  {/* Rounded photo */}
                  <div className="relative aspect-[4/3] rounded-[2rem] overflow-hidden border border-slate-200 shadow-lg bg-slate-100">
                    <img
                      src={latestArrival.image || (latestArrival.images && latestArrival.images[0])}
                      alt={`${latestArrival.year} ${latestArrival.make} ${latestArrival.model}`}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute top-4 left-4 bg-emerald-600 text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full shadow-md">
                      {t('hero.latestArrivalBadge', 'Dernier arrivage')}
                    </div>
                  </div>

                  {/* White info card */}
                  <div className="relative -mt-10 mx-4 sm:mx-6 bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 shadow-xl group-hover:shadow-2xl transition-shadow">
                    <div className="flex items-center justify-between gap-3 mb-1">
                      <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                        {latestArrival.year}
                      </p>
                      {latestArrival.price > 0 && (
                        <p className="text-[11px] text-emerald-700 font-medium whitespace-nowrap bg-emerald-50 border border-emerald-100 px-2.5 py-0.5 rounded-full">
                          {t('hero.fromMonth', 'dès')} <span className="font-bold">€{Math.round(estimateMonthly(latestArrival.price, latestArrival.year).monthly).toLocaleString('fr-BE')}</span>{t('hero.perMonth', '/mois')}
                        </p>
                      )}
                    </div>
                    <div className="flex items-end justify-between gap-3 mb-3">
                      <h3 className="text-base sm:text-lg font-bold leading-tight text-slate-900 truncate flex-1 min-w-0" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {latestArrival.make} {latestArrival.model}
                      </h3>
                      <p className="text-2xl sm:text-3xl font-extrabold leading-none text-blue-900 flex-shrink-0" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        €{(latestArrival.price || 0).toLocaleString('fr-BE')}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-600 pt-3 border-t border-slate-100">
                      <span className="inline-flex items-center gap-1.5"><Gauge size={13} className="text-slate-400" /> {(latestArrival.mileage || 0).toLocaleString('fr-BE')} km</span>
                      <span className="inline-flex items-center gap-1.5"><Fuel size={13} className="text-slate-400" /> <span className="capitalize">{latestArrival.fuel}</span></span>
                      <span className="inline-flex items-center gap-1.5"><Settings2 size={13} className="text-slate-400" /> <span className="capitalize">{latestArrival.transmission}</span></span>
                    </div>
                  </div>
                </Link>
              ) : (
                /* Skeleton (no Porsche fallback) */
                <div className="aspect-[4/3] rounded-[2rem] bg-slate-100 shimmer flex items-center justify-center">
                  <Car size={56} className="text-slate-300" strokeWidth={1.4} />
                </div>
              )}
            </div>
          </div>

          {/* Inline search bar */}
          <form
            onSubmit={onSearch}
            className="mt-12 lg:mt-16 bg-white border border-slate-200 rounded-2xl shadow-md p-3 flex flex-col sm:flex-row gap-2"
            data-testid="hero-search-form"
          >
            <div className="flex-1 flex items-center gap-2 px-4 py-2 sm:border-r sm:border-slate-100">
              <Search size={18} className="text-slate-400 flex-shrink-0" />
              <input
                type="text"
                value={searchMake}
                onChange={(e) => setSearchMake(e.target.value)}
                placeholder={t('search.makeModel', 'Marque ou modèle (ex: Audi, BMW…)')}
                className="w-full bg-transparent outline-none text-sm text-slate-900 placeholder:text-slate-400"
                data-testid="hero-search-make"
              />
            </div>
            <div className="flex-1 flex items-center gap-2 px-4 py-2">
              <CreditCard size={18} className="text-slate-400 flex-shrink-0" />
              <input
                type="number"
                value={searchMaxPrice}
                onChange={(e) => setSearchMaxPrice(e.target.value)}
                placeholder={t('search.maxPrice', 'Budget maximum (€)')}
                className="w-full bg-transparent outline-none text-sm text-slate-900 placeholder:text-slate-400"
                data-testid="hero-search-price"
              />
            </div>
            <button type="submit" className="btn-primary sm:px-6" data-testid="hero-search-submit">
              {t('search.cta', 'Rechercher')}
              <ArrowRight size={16} />
            </button>
          </form>
        </div>
      </section>

      {/* ===== FEATURED INVENTORY ===== */}
      <section className="py-16 sm:py-20" data-testid="featured-inventory">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
            <div>
              <span className="section-eyebrow">{t('featured.eyebrow', 'Notre inventaire')}</span>
              <h2 className="mt-4 text-3xl sm:text-4xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {t('featured.title', 'Véhicules disponibles')}
              </h2>
            </div>
            <Link to="/inventory" className="btn-secondary text-sm" data-testid="featured-see-all">
              {t('featured.seeAll', 'Voir tout l\'inventaire')}
              <ArrowRight size={14} />
            </Link>
          </div>

          {isLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="rounded-2xl border border-slate-200 overflow-hidden bg-white">
                  <div className="aspect-[4/3] shimmer" />
                  <div className="p-5 space-y-3">
                    <div className="h-5 w-3/4 shimmer rounded" />
                    <div className="h-7 w-1/2 shimmer rounded" />
                    <div className="h-4 w-full shimmer rounded" />
                  </div>
                </div>
              ))}
            </div>
          ) : featured.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center" data-testid="no-cars">
              <Car size={40} className="text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600">{t('featured.empty', "Aucun véhicule disponible pour le moment. Revenez bientôt !")}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {featured.map((car, i) => (
                <CarCard key={car.id} car={car} priority={i < 4} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ===== TRUST / WHY US ===== */}
      <section className="py-16 sm:py-20 bg-white border-y border-slate-200" data-testid="why-us">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="section-eyebrow">{t('why.eyebrow', 'Nos engagements')}</span>
            <h2 className="mt-4 text-3xl sm:text-4xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {t('why.title2', 'Pourquoi choisir AutoDistrict ?')}
            </h2>
            <p className="mt-4 text-slate-600 max-w-xl mx-auto">
              {t('why.subtitle2', 'Chaque véhicule est contrôlé, garanti et accompagné. Notre métier, c\'est votre tranquillité.')}
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 sm:gap-6">
            {trustItems.map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="bg-[#F7F8FA] rounded-2xl p-5 border border-slate-100 hover:bg-white hover:shadow-md transition-all" data-testid={`why-item-${i}`}>
                  <div className="w-11 h-11 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center text-emerald-600 mb-4">
                    <Icon size={20} />
                  </div>
                  <h3 className="text-sm sm:text-base font-bold text-slate-900 mb-1.5" style={{ fontFamily: 'Outfit, sans-serif' }}>{item.title}</h3>
                  <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== TESTIMONIALS ===== */}
      <Testimonials />

      {/* ===== SELL YOUR CAR BANNER ===== */}
      <section className="py-16 sm:py-20" data-testid="sell-banner">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative bg-gradient-to-br from-blue-900 to-blue-800 rounded-3xl overflow-hidden">
            <div className="absolute inset-0 opacity-10" style={{
              backgroundImage: 'radial-gradient(circle at 20% 50%, white 0%, transparent 30%), radial-gradient(circle at 80% 50%, white 0%, transparent 30%)',
            }} />
            <div className="relative px-8 py-12 sm:px-14 sm:py-16 grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-8 items-center">
              <div className="text-white">
                <span className="inline-block bg-emerald-500 text-white text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full mb-4">
                  {t('sellbanner.eyebrow', 'Vous vendez ?')}
                </span>
                <h2 className="text-3xl sm:text-4xl font-bold mb-4 max-w-2xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {t('sellbanner.title', 'Revendez votre voiture en toute sérénité')}
                </h2>
                <p className="text-blue-100 max-w-xl">
                  {t('sellbanner.desc', "Estimation gratuite, vente accompagnée, présentation professionnelle. Vous focalisez sur le résultat, nous gérons le reste.")}
                </p>
              </div>
              <div className="flex flex-wrap gap-3 lg:flex-col">
                <Link to="/estimer-mon-vehicule" className="inline-flex items-center justify-center gap-2 bg-white text-blue-900 hover:bg-blue-50 px-6 py-3.5 rounded-full font-semibold text-sm transition-colors" data-testid="sellbanner-cta-primary">
                  {t('sellbanner.cta1', 'Faites estimer votre véhicule')}
                  <ArrowRight size={16} />
                </Link>
                <Link to="/sell-your-car" className="inline-flex items-center justify-center gap-2 bg-transparent text-white border border-white/40 hover:bg-white/10 px-6 py-3.5 rounded-full font-semibold text-sm transition-colors" data-testid="sellbanner-cta-secondary">
                  {t('sellbanner.cta2', 'En savoir plus')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== CONTACT / VISIT ===== */}
      <section className="pb-20" data-testid="contact-visit">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl border border-slate-200 p-8 sm:p-12 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <span className="section-eyebrow">{t('visit.eyebrow', 'Sur rendez-vous')}</span>
              <h2 className="mt-4 text-3xl sm:text-4xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {t('visit.title', 'Découvrez nos véhicules en personne')}
              </h2>
              <p className="mt-4 text-slate-600 leading-relaxed">
                {t('visit.desc', "Notre garage à porte fermée vous accueille uniquement sur rendez-vous, pour un accompagnement personnalisé et sans pression commerciale.")}
              </p>
              <div className="mt-6 space-y-3">
                <a href="tel:+32489432161" className="flex items-center gap-3 text-slate-900 font-semibold">
                  <Phone size={18} className="text-blue-900" />
                  +32 489 43 21 61
                </a>
                <div className="flex items-center gap-3 text-slate-700">
                  <MapPin size={18} className="text-blue-900" />
                  {t('location.address', 'Wavre, Belgique')}
                </div>
              </div>
              <div className="mt-7 flex flex-wrap gap-3">
                <Link to="/booking" className="btn-primary" data-testid="visit-book">
                  {t('visit.book', 'Prendre rendez-vous')}
                  <ArrowRight size={16} />
                </Link>
                <Link to="/contact" className="btn-secondary" data-testid="visit-contact">
                  {t('visit.contact', 'Nous contacter')}
                </Link>
              </div>
            </div>
            <div className="aspect-video rounded-2xl overflow-hidden border border-slate-200">
              <img
                src="https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&w=1400&q=80"
                alt="Poignée de main entre deux personnes"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
